import {useStore} from '@/store/root-store';
import {opaqueColor} from '@/utils/colors';
import {observer} from 'mobx-react';
import {useEffect, useState} from 'react';
import styled from 'styled-components';

interface SingleTermProps {
  term: any;
}

export const SingleTerm = observer(({term}: SingleTermProps) => {
  const {
    contentOptimizer: {
      currentPage: {
        stagingTaSuggestions,
        setTaActiveTerm,
        taActiveTerm,
        highlightedNodeId,
      },
    }} = useStore('');

  const [dots, setDots] = useState({
    total: 0,
    saved: 0,
    dismissed: 0,
  });

  useEffect(() => {
    const savedPlacements = stagingTaSuggestions?.filter(suggestion => suggestion.termIncluded?.includes(term.name) && suggestion.status == 'accepted');
    const dismissedPlacements = stagingTaSuggestions?.filter(suggestion => suggestion.termIncluded?.includes(term.name) && suggestion.status == 'dismissed');

    setDots(() => {
      return {
        total: term.placements,
        saved: savedPlacements?.length ?? 0,
        dismissed: dismissedPlacements?.length ?? 0,
      };
    });
  }, [...stagingTaSuggestions]);


  const generatePlacmenetsInfo = dots => {
    if (dots.saved == 0 && dots.dismissed == 0) return `${dots.total} available placements`;

    let response = '';
    if ((dots.total - dots.dismissed - dots.saved) > 0) response = ` ‧ ${dots.total} available`;
    if (dots.dismissed) response = `${dots.dismissed} dismissed ${response}`;
    if (dots.saved) response = `${dots.saved} accepted  ${dots.dismissed > 0 ? ` ‧ ` : ``} ${response}`;
    return response;
  };

  const onTermClickHandler = curTerm => {
    if (!taActiveTerm?.term || taActiveTerm.term !== curTerm.name) {
      setTaActiveTerm({
        class: curTerm.name.replaceAll(' ', '-'),
        term: curTerm.name,
        order: 0,
      });
    } else {
      const totalOccurances = document.getElementsByClassName(taActiveTerm.class)?.length;

      const updatedValues = {
        class: taActiveTerm.class,
        term: taActiveTerm.term,
        order: taActiveTerm.order < totalOccurances - 1 ? taActiveTerm.order + 1 : 0,
      };

      setTaActiveTerm(updatedValues);
    }
  };

  return <Container onClick={() => onTermClickHandler(term)} className={taActiveTerm?.term == term.name ? 'isActive' : ''}>
    <div style={{width: '70%'}}>
      <p className='termName'>{term.name}</p>
      <Data>
        <PlacementsInfo>
          {generatePlacmenetsInfo(dots)}
        </PlacementsInfo>
      </Data>
    </div>
    <DotsContainer>
      {stagingTaSuggestions?.filter(suggestion => suggestion.termIncluded?.includes(term.name))?.sort(item => item.status == 'accepted' ? -1 : item.status == 'default' ? 0 : 1)?.map((suggestion, idx) => {
        if (suggestion.status == 'default') return <Dot color='#A3A4A4' key={idx} isActive={highlightedNodeId == suggestion.itemIndex}/>;
        if (suggestion.status == 'dismissed') return <Dot color='#F44343' key={idx} isActive={highlightedNodeId == suggestion.itemIndex}/>;
        if (suggestion.status == 'accepted') return <Dot color='#1FAC47' key={idx} isActive={highlightedNodeId == suggestion.itemIndex}/>;
      })}
    </DotsContainer>
  </Container>;
});

const Container = styled.div`
  width: 100%;
  padding: 8px 16px;
  margin-top: 4px;
  margin-bottom: 4px;
  background-color: ${opaqueColor('#4E5156', 30)};
  border-radius: 10px;
  cursor: pointer;
  transition: background .3s ease-in;
  display: flex;

  .termName { 
    margin: 0px;
  }

  &.isActive {
    background: linear-gradient(90.16deg, ${opaqueColor('#E15E83', 20)} 2.81%, ${opaqueColor('#F58069', 20)} 96.58%);

    .termName {
      background: linear-gradient(90.16deg, #E15E83 2.81%, #F58069 96.58%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  
  &:hover {
    background: linear-gradient(90.16deg, ${opaqueColor('#E15E83', 20)} 2.81%, ${opaqueColor('#F58069', 20)} 96.58%);

    .termName {
      background: linear-gradient(90.16deg, #E15E83 2.81%, #F58069 96.58%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
`;

const Data = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PlacementsInfo = styled.div`
  font-size: 12px;
  color: #A3A4A4 !important;
`;

const DotsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 28%;
  margin-left: 2%;
  margin-top: auto;
  // flex-direction: row-reverse;
`;

const Dot = styled.div<{color: string; isActive: boolean}>`
  width: 7px;
  height: 7px;
  margin-right: 5px;
  margin-bottom: 5px;
  border-radius: 100%;
  background-color: ${p => p.color};
  position: relative;

  &::after {
    content: '';
    display: ${p => p.isActive ? 'block' : 'none'};
    width: 11px;
    height: 11px;
    border-radius: 100%;
    position: absolute;
    top: -2px;
    left: -2px;
    background-color: transparent;
    border: 1px solid red;
  }
`;


